import { createSelector } from 'reselect';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import {
  accessingSpaceAsStaff,
  isInstructorInSpace,
} from '@core/selectors/classroom';
import { getAllSpaces } from '@core/selectors/space/space.selectors';
import { currentUser, currentUserId } from '@core/selectors/user';
import { AulaState, Space } from '@core/types/state';
import { HomeViewRole } from '@app/constants/home';
import getStarredSpaces from '@core/spaceSelector/utils/getStarredSpaces';
import { mapHomeSpaceToCard } from '@app/utils/mapHomeSpaceToCard';
import { SpaceWidgetCardData } from '@app/components/presentational/Home/widgets/spaceWidget/SpaceWidget.types';

export const getHomeState = (state: AulaState) => state.home;

/**
 * Opening a space the user only has staff access to adds it to
 * `data.classRooms`, but it is not one of the user's own spaces. Home returns
 * `null` for those so they count as neither an Educator nor a Student space.
 */
const getUserRoleInSpace = (
  state: AulaState,
  spaceId: string,
  userId: string | null
): HomeViewRole | null => {
  if (!userId) {
    return STUDENT_ROLE;
  }

  if (isInstructorInSpace(state)(userId, spaceId)) {
    return EDUCATOR_ROLE;
  }

  if (accessingSpaceAsStaff(state)(spaceId)) {
    return null;
  }

  return STUDENT_ROLE;
};

const getHomeSpaceRoles = createSelector(
  [(state: AulaState) => state, getAllSpaces, currentUserId],
  (state, spaces, userId) => {
    let hasEducatorSpace = false;
    let hasStudentSpace = false;

    if (userId) {
      Object.values(spaces || {}).forEach((space) => {
        const role = getUserRoleInSpace(state, space.objectId, userId);

        if (role === EDUCATOR_ROLE) {
          hasEducatorSpace = true;
        } else if (role === STUDENT_ROLE) {
          hasStudentSpace = true;
        }
      });
    }

    return { hasEducatorSpace, hasStudentSpace };
  }
);

export const getDefaultHomeViewRole = (state: AulaState): HomeViewRole =>
  getHomeSpaceRoles(state).hasEducatorSpace ? EDUCATOR_ROLE : STUDENT_ROLE;

export const getHomeViewRole = (state: AulaState): HomeViewRole => {
  const selectedRole = getHomeState(state)?.viewRole;

  if (!selectedRole) {
    return getDefaultHomeViewRole(state);
  }

  const { hasEducatorSpace, hasStudentSpace } = getHomeSpaceRoles(state);
  const spacesLoaded = hasEducatorSpace || hasStudentSpace;
  const hasSpacesForSelectedRole =
    selectedRole === EDUCATOR_ROLE ? hasEducatorSpace : hasStudentSpace;

  // Keep the selected role while spaces load, but drop it once we know the user
  // has no spaces for it (e.g. a persisted Educator role for a staff-only user).
  if (!spacesLoaded || hasSpacesForSelectedRole) {
    return selectedRole;
  }

  return getDefaultHomeViewRole(state);
};

export const getCanSwitchHomeViewRole = (state: AulaState): boolean => {
  const { hasEducatorSpace, hasStudentSpace } = getHomeSpaceRoles(state);

  return hasEducatorSpace && hasStudentSpace;
};

/**
 * Home view role depends on membership spaces from startup. Before
 * `hasInitialData`, `getHomeViewRole` can default to student and later flip
 * (e.g. to educator) once classRooms arrive — which mis-tags tour analytics.
 */
export const isHomeViewRoleReady = (state: AulaState): boolean =>
  Boolean(state.data?.hasInitialData);

export const getHomeSpacesForView = createSelector(
  [(state: AulaState) => state, getHomeViewRole],
  (state, viewRole): Space[] => {
    const userId = currentUserId(state);
    return Object.values(getAllSpaces(state) || {})
      .filter((space) => !space.archived)
      .filter((space) => {
        const role = getUserRoleInSpace(state, space.objectId, userId);
        // Staff-access spaces are not Educator/Student membership, but they still
        // need to be available to Home consumers like recently viewed (via this
        // selector) after the user has opened them.
        return role === viewRole || role === null;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }
);

export const getHomeUserFirstName = (state: AulaState): string =>
  currentUser(state)?.firstName || '';

export const getHomeUserPreferences = createSelector(
  [(state: AulaState) => state.home],
  (home) => home?.widgetPreferences || []
);

export const getHomeSpacesState = (state: AulaState) => getHomeState(state);

const getActiveHomeSpacesRoleState = createSelector(
  [getHomeState, getHomeViewRole],
  (home, viewRole) => home.spacesByRole[viewRole]
);

export const getHomeSpacesLoading = (state: AulaState): boolean =>
  getActiveHomeSpacesRoleState(state)?.loading ?? false;

export const getHomeSpacesError = (state: AulaState): boolean =>
  getActiveHomeSpacesRoleState(state)?.error ?? false;

export const getHomeSpacesErrorType = (
  state: AulaState
): 'network' | 'api' | null =>
  getActiveHomeSpacesRoleState(state)?.errorType ?? null;

export const getHomeSpacesHasMore = (state: AulaState): boolean =>
  getActiveHomeSpacesRoleState(state)?.hasMore ?? true;

type RecentlyViewedSpaceItem = {
  spaceId?: string;
  lastNavigation?: string;
};

export const getRecentlyViewedNavigationBySpaceId = (
  state: AulaState
): Record<string, string> => {
  const spaceList = (state.spaceSelector?.recentlyViewedSpaces?.spaceList ??
    []) as RecentlyViewedSpaceItem[];

  return spaceList.reduce<Record<string, string>>((acc, item) => {
    if (item?.spaceId && item?.lastNavigation) {
      acc[item.spaceId] = item.lastNavigation;
    }
    return acc;
  }, {});
};

export const getHomeSpaceCardsForView = createSelector(
  [getHomeState, getHomeViewRole, getRecentlyViewedNavigationBySpaceId],
  (home, viewRole, lastNavBySpaceId): SpaceWidgetCardData[] => {
    if (!home) {
      return [];
    }

    const roleState = home.spacesByRole[viewRole];

    return roleState.spaces.map((space) => {
      const card = mapHomeSpaceToCard(
        space,
        roleState.progressById[space.spaceId],
        roleState.engagementById[space.spaceId]
      );

      return {
        ...card,
        lastNavigation: lastNavBySpaceId[space.spaceId] ?? null,
        isRefreshingCardContent:
          roleState.loadingCardMetricsSpaceIds.includes(space.spaceId) ||
          roleState.refreshingCardDataSpaceId === space.spaceId,
      };
    });
  }
);

export const getHomeStarredSpaces = createSelector(
  [(state: AulaState) => state, getHomeViewRole, getAllSpaces, currentUserId],
  (state, viewRole, spacesMap, userId): Space[] => {
    const spaces = Object.values(spacesMap || {});

    // Filter starred spaces by archived status and the active viewRole (Educator/Student)
    const filteredSpaces = spaces
      .filter((space) => !space.archived)
      .filter(
        (space) =>
          getUserRoleInSpace(state, space.objectId, userId) === viewRole
      );

    return getStarredSpaces(filteredSpaces);
  }
);
